import { useEffect, useState } from 'react'
import { Check, Eye, ScreenShare, ScreenShareOff, X } from 'lucide-react'
import type { MeekijkVerzoek } from '../../../gedeeld/types'
import { api } from '../../lib/api'
import { startPoller } from '../../lib/meekijken'
import { navigeer, useRoute } from '../../lib/router'
import { meldFout } from '../../lib/staat'
import { Knop, Venster } from '../ui'
import { startDelen, stopDelen, useDelen } from './delen'

/* ===========================================================================
 *  Meekijken in de Shell: de vraag en de balk
 *
 *  MeekijkMelding  Een stille poller (elke 5 s, alleen als het tabblad
 *                  zichtbaar is) op meekijk-voor-mij. Staat er een verzoek,
 *                  dan een venster: "<naam> wil meekijken op je scherm:
 *                  <reden>. Accepteren / Weigeren". Stil: een fout van de
 *                  server komt niet in beeld, de volgende ronde probeert het
 *                  opnieuw.
 *  DeelBalk        Zolang je deelt, bovenaan: "Je deelt je scherm met
 *                  <naam> -- Stoppen". Op elk scherm van IT.
 * =========================================================================== */

export function MeekijkMelding() {
  const { pad } = useRoute()
  const delen = useDelen()
  const [verzoeken, setVerzoeken] = useState<MeekijkVerzoek[]>([])
  const [weg, setWeg] = useState<Set<string>>(() => new Set())
  const [bezig, setBezig] = useState<'ja' | 'nee' | null>(null)

  useEffect(() => startPoller(async () => {
    setVerzoeken(await api.meekijkVoorMij())
  }, { doc: document }), [])

  /* Niet het verzoek waar je al naar kijkt, niet wat je wegklikte, niet
     terwijl je al deelt, en niet wat al verlopen is. */
  const nu = Date.now()
  const v = verzoeken.find((x) => !weg.has(x.id) && pad !== `/meekijken/${x.id}` && x.verloopt > nu)
  if (!v || (delen && delen.fase !== 'gestopt' && delen.fase !== 'mislukt')) return null

  const later = () => setWeg((w) => new Set(w).add(v.id))

  function accepteren() {
    if (!v) return
    setBezig('ja')
    /* Eerst het scherm kiezen, in deze klik (zie delen.ts); daarna het scherm
       /meekijken/<id>, waar de stand te zien is. */
    const id = v.id
    void startDelen(id, v.vragerNaam).finally(() => setBezig(null))
    /* Meteen erheen: daar staat hoe het gaat, en als het kiezen mislukte
       waarom, met de knoppen opnieuw. */
    setWeg((w) => new Set(w).add(id))
    navigeer(`/meekijken/${id}`)
  }

  async function weigeren() {
    if (!v) return
    setBezig('nee')
    try {
      await api.meekijkAntwoord(v.id, false)
      setWeg((w) => new Set(w).add(v.id))
    } catch (e) { meldFout(e) } finally { setBezig(null) }
  }

  return (
    <Venster titel={<><Eye size={18} /> Meekijken</>} onSluit={later} bewustSluiten
      voet={<>
        <Knop soort="stil" onClick={later} disabled={bezig !== null}>Later</Knop>
        <Knop soort="gewoon" icoon={<X size={15} />} bezig={bezig === 'nee'} disabled={bezig !== null} onClick={() => void weigeren()}>Weigeren</Knop>
        <Knop soort="hoofd" icoon={<Check size={15} />} bezig={bezig === 'ja'} disabled={bezig !== null} onClick={accepteren}>Accepteren</Knop>
      </>}>
      <p><b>{v.vragerNaam}</b> wil meekijken op je scherm: “{v.reden}”.</p>
      <p className="veld-hulp">
        Zeg je ja, dan kies je zelf wat je deelt: één venster, een tabblad of je hele scherm. {v.vragerNaam} kan alleen
        kijken, niet klikken of typen, en je kunt altijd stoppen.
      </p>
    </Venster>
  )
}

export function DeelBalk() {
  const delen = useDelen()
  if (!delen || delen.fase === 'gestopt' || delen.fase === 'mislukt') return null
  return (
    <div className="deel-balk" role="status">
      <ScreenShare size={16} />
      <span>
        {delen.fase === 'kiezen' ? `Kies wat je met ${delen.naam} wilt delen…`
          : delen.fase === 'delen' ? <>Je deelt je scherm met <b>{delen.naam}</b></>
          : <>Je scherm wordt gedeeld met <b>{delen.naam}</b> (verbinden…)</>}
      </span>
      <button type="button" className="knop k-gevaar klein" onClick={() => void stopDelen()}>
        <ScreenShareOff size={14} /> Stoppen
      </button>
    </div>
  )
}
